import React from 'react';

import { get as getConstants } from '@lumapps/constants';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { useSelector, useStore } from '@lumapps/redux/react';
import { Route, Link as RouterLink, useRouter } from '@lumapps/router';
import { AppId } from '@lumapps/router/constants';
import { useTranslate } from '@lumapps/translations';

import { getNotificationMeta } from '../notification-meta';
import { Notification, NotificationMetaLinkAs } from '../types';
import { isModernNotification } from '../utils/isModernNotification';

export interface UseNotificationRoute {
    /** props to be passed in as the attributes to be used on the link */
    linkProps: Record<string, any>;
    /** route for the notification content */
    route: Route | null;
    /** specific component that will be used for rendering the link. */
    linkAs?: 'a' | any;
}

/**
 * Strips the leading `/{instanceSlug}` segment off a same-instance `targetUrl`.
 * Our own app routes never include the instance slug themselves — the router adds it back
 * automatically based on whichever site is currently active — so it has to be removed first.
 *
 * Example: "/home/ls/canonical/task/f47ac10b-..."
 *   1. split('/')  -> ['', 'home', 'ls', 'canonical', 'task', 'f47ac10b-...']
 *   2. slice(2)    -> ['ls', 'canonical', 'task', 'f47ac10b-...']  (drops '' and the 'home' slug)
 *   3. join('/')   -> 'ls/canonical/task/f47ac10b-...'
 *   4. add '/'     -> '/ls/canonical/task/f47ac10b-...'
 */
const stripInstanceSlug = (path: string) => `/${path.split('/').slice(2).join('/')}`;

/**
 * Returns the necessary props to render a link towards a Notification.
 */
export const useNotificationRoute = (notification: Notification): UseNotificationRoute => {
    const translation = useTranslate();
    const { isLegacyContext, applicationId: currentApplicationId } = getConstants();
    const currentInstanceId = useSelector(instanceIdSelector);
    const { getUrl } = useRouter();
    const { getState } = useStore<FrontOfficeStore>();

    /**
     * TODO: remove this once we have migrated to REACT
     */
    const target = React.useMemo(() => {
        if (isLegacyContext) {
            return '_self';
        }
        return undefined;
    }, [isLegacyContext]);

    return React.useMemo(() => {
        if (isModernNotification(notification)) {
            if (!notification.targetUrl) {
                return { linkProps: {}, route: null };
            }

            const isCrossInstance = Boolean(
                notification.instanceId && currentInstanceId && notification.instanceId !== currentInstanceId,
            );

            if (isCrossInstance) {
                // Not truly external: it's an in-app destination on another instance, so it stays in the same tab.
                const route: Route = { path: notification.targetUrl, appId: AppId.external };
                return {
                    linkProps: { href: notification.targetUrl, target },
                    route,
                    linkAs: 'a',
                };
            }

            const route: Route = { path: stripInstanceSlug(notification.targetUrl), appId: currentApplicationId };
            return {
                linkProps: { to: route, target },
                route,
                linkAs: RouterLink,
            };
        }

        const { type } = notification;
        const meta = getNotificationMeta(type);

        if (meta && meta.getRoute) {
            const route = meta.getRoute({
                notification,
                translation,
                currentInstanceId,
                getState,
            });

            const { linkAs, ...linkAsProps }: Partial<NotificationMetaLinkAs> =
                meta.getLinkAs?.({ translation, notification }) || {};

            if (route) {
                const isExternal = route.appId === AppId.external;
                const linkProps = {
                    href: getUrl({ ...route, instanceSlug: notification.instanceDetails?.slug }),
                    target: isExternal ? '_blank' : target,
                    rel: isExternal ? 'noopener noreferrer' : undefined,
                    ...linkAsProps,
                };

                /** if a correct link has been generated, use RouterLink for better router management */
                const defaultLinkElement = linkAsProps?.to ? RouterLink : 'a';

                return {
                    linkProps,
                    route,
                    linkAs: linkAs || defaultLinkElement,
                };
            }
        }

        return { linkProps: {}, route: null };
    }, [notification, translation, currentInstanceId, getState, getUrl, target, currentApplicationId]);
};
